'use client';

import { useShippingSLA } from '@skin-seoul/service';
import type { Country } from '@skin-seoul/service';
import { ShippingSLA } from './ShippingSLA';
import { ShippingSLASummary } from './ShippingSLASummary';

export type ShippingSLAVariant = 'default' | 'summary';

export interface ShippingSLAContainerProps {
  /** Destination country — resolves the correct SLA entry */
  country: Country;
  /**
   * Presentation variant:
   *  - `default` — full SLA block used on PDP / Cart drawer.
   *  - `summary` — 2-line condensed variant for the checkout Order Summary card.
   *
   * Custom layouts (e.g. checkout Shipping section card) compose `<ShippingSLA>`
   * directly with the `useShippingSLA` hook instead of using this container.
   */
  variant?: ShippingSLAVariant;
  /**
   * Fallback seasonal notice when the backend does not emit `systemMessage`.
   * Used today for the hardcoded Korean holiday banner until the backend
   * takes over seasonal overrides.
   */
  seasonalNoticeFallback?: string;
  /** Additional wrapper classes */
  className?: string;
}

/**
 * Suspense-backed preset for the default SLA composition. Must be rendered
 * inside a `QueryBoundary` / `<Suspense>` whose `loadingFallback` matches
 * the chosen variant skeleton.
 */
export const ShippingSLAContainer = ({
  country,
  variant = 'default',
  seasonalNoticeFallback,
  className,
}: ShippingSLAContainerProps) => {
  const { formatted } = useShippingSLA(country);

  if (!formatted) {
    return null;
  }

  const finalFormatted =
    seasonalNoticeFallback && !formatted.seasonalNoticeText
      ? { ...formatted, seasonalNoticeText: seasonalNoticeFallback }
      : formatted;

  if (variant === 'summary') {
    return <ShippingSLASummary formatted={finalFormatted} className={className} />;
  }

  return (
    <ShippingSLA formatted={finalFormatted} className={className}>
      <ShippingSLA.Header />
      <ShippingSLA.Dates />
      <ShippingSLA.Footnote />
      <ShippingSLA.Pill />
    </ShippingSLA>
  );
};
